<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { promptProposalsAPI } from 'dashboard/api/iaReports';

const statusFilter = ref('');
const loading = ref(false);
const proposals = ref([]);
const expandedId = ref(null);
const expandedData = ref(null);
const loadingDetail = ref(false);
const actionLoading = ref(new Set());

const STATUS_STYLES = {
  pending: { label: 'Pendente', color: 'text-amber-600', bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200', icon: 'i-lucide-clock' },
  approved: { label: 'Aprovada', color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200', icon: 'i-lucide-check' },
  rejected: { label: 'Rejeitada', color: 'text-n-slate-9', bg: 'bg-n-alpha-2', border: 'border-n-weak', icon: 'i-lucide-x' },
  applied: { label: 'Aplicada', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200', icon: 'i-lucide-check-circle' },
  rolled_back: { label: 'Revertida', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200', icon: 'i-lucide-undo-2' },
};

const TYPE_LABELS = {
  objection_handling: 'Gestao de Objecoes',
  upsell: 'Upsell',
  follow_up: 'Follow-up',
  qualification: 'Qualificacao',
  spin: 'SPIN Selling',
  general: 'Geral',
  closing: 'Fechamento',
  rapport: 'Rapport',
};

function statusStyle(s) {
  return STATUS_STYLES[s] || STATUS_STYLES.pending;
}

function typeLabel(t) {
  return TYPE_LABELS[t] || t;
}

function confidenceColor(c) {
  if (c >= 0.7) return 'text-emerald-600';
  if (c >= 0.4) return 'text-amber-600';
  return 'text-red-600';
}

function formatDate(ts) {
  if (!ts) return '-';
  return new Date(ts * 1000).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}

async function fetchProposals() {
  loading.value = true;
  try {
    const params = {};
    if (statusFilter.value) params.status = statusFilter.value;
    const res = await promptProposalsAPI.getProposals(params);
    proposals.value = res.data?.proposals ?? [];
  } catch {
    proposals.value = [];
  } finally {
    loading.value = false;
  }
}

async function toggleExpand(proposal) {
  if (expandedId.value === proposal.id) {
    expandedId.value = null;
    expandedData.value = null;
    return;
  }
  expandedId.value = proposal.id;
  loadingDetail.value = true;
  try {
    const res = await promptProposalsAPI.getProposal(proposal.id);
    expandedData.value = res.data?.proposal ?? null;
  } catch {
    expandedData.value = null;
  } finally {
    loadingDetail.value = false;
  }
}

async function doAction(id, action) {
  actionLoading.value.add(id);
  try {
    if (action === 'approve') await promptProposalsAPI.approve(id);
    else if (action === 'reject') await promptProposalsAPI.reject(id);
    else if (action === 'apply') await promptProposalsAPI.apply(id);
    else if (action === 'rollback') await promptProposalsAPI.rollback(id);
    await fetchProposals();
    if (expandedId.value === id) {
      const res = await promptProposalsAPI.getProposal(id);
      expandedData.value = res.data?.proposal ?? null;
    }
  } catch {
    // ignore
  } finally {
    actionLoading.value.delete(id);
  }
}

onMounted(fetchProposals);
watch(statusFilter, () => fetchProposals());

const stats = computed(() => {
  const all = proposals.value;
  return {
    total: all.length,
    pending: all.filter(p => p.status === 'pending').length,
    applied: all.filter(p => p.status === 'applied').length,
    rejected: all.filter(p => p.status === 'rejected').length,
  };
});
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h1 class="text-xl font-bold text-n-slate-12">Propostas de Melhoria</h1>
        <p class="text-sm text-n-slate-10 mt-0.5">
          Sugestoes automaticas de melhoria no prompt geradas pela analise de performance
        </p>
      </div>

      <div class="flex items-center gap-2">
        <select
          v-model="statusFilter"
          class="text-sm bg-white dark:bg-n-alpha-2 border border-n-weak rounded-lg px-3 py-2 text-n-slate-12 outline-none"
        >
          <option value="">Todos os status</option>
          <option value="pending">Pendentes</option>
          <option value="approved">Aprovadas</option>
          <option value="applied">Aplicadas</option>
          <option value="rejected">Rejeitadas</option>
          <option value="rolled_back">Revertidas</option>
        </select>
      </div>
    </div>

    <!-- Stats -->
    <div class="grid grid-cols-4 gap-3">
      <div class="flex flex-col gap-1 p-3 rounded-xl border border-n-weak bg-n-solid-2 shadow-sm">
        <p class="text-xs text-n-slate-10">Total</p>
        <p class="text-xl font-bold text-n-slate-12">{{ stats.total }}</p>
      </div>
      <div class="flex flex-col gap-1 p-3 rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/20 shadow-sm">
        <p class="text-xs text-n-slate-10">Pendentes</p>
        <p class="text-xl font-bold text-amber-600">{{ stats.pending }}</p>
      </div>
      <div class="flex flex-col gap-1 p-3 rounded-xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-900/20 shadow-sm">
        <p class="text-xs text-n-slate-10">Aplicadas</p>
        <p class="text-xl font-bold text-emerald-600">{{ stats.applied }}</p>
      </div>
      <div class="flex flex-col gap-1 p-3 rounded-xl border border-n-weak bg-n-alpha-2 shadow-sm">
        <p class="text-xs text-n-slate-10">Rejeitadas</p>
        <p class="text-xl font-bold text-n-slate-9">{{ stats.rejected }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center py-16">
      <span class="i-lucide-loader-2 w-6 h-6 text-n-slate-10 animate-spin" />
      <span class="ml-2 text-n-slate-10 text-sm">Carregando propostas...</span>
    </div>

    <!-- Empty -->
    <div v-else-if="proposals.length === 0" class="flex flex-col items-center py-16 text-n-slate-10">
      <span class="i-lucide-lightbulb w-12 h-12 mb-3 opacity-40" />
      <p class="text-sm">Nenhuma proposta de melhoria encontrada.</p>
      <p class="text-xs mt-1">Propostas sao geradas automaticamente toda semana pela analise de performance.</p>
    </div>

    <!-- Proposals List -->
    <div v-else class="flex flex-col gap-3">
      <div
        v-for="proposal in proposals"
        :key="proposal.id"
        class="rounded-xl border border-n-weak bg-n-solid-2 shadow-sm overflow-hidden"
      >
        <!-- Card Header -->
        <div
          class="flex items-center gap-3 p-4 cursor-pointer hover:bg-n-alpha-1 transition-colors"
          @click="toggleExpand(proposal)"
        >
          <span
            :class="[expandedId === proposal.id ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right', 'w-4 h-4 text-n-slate-10 shrink-0']"
          />

          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <p class="text-sm font-semibold text-n-slate-12 truncate">{{ proposal.title }}</p>
              <span
                :class="[statusStyle(proposal.status).bg, statusStyle(proposal.status).border, statusStyle(proposal.status).color, 'text-xs px-2 py-0.5 rounded-full border font-medium']"
              >
                {{ statusStyle(proposal.status).label }}
              </span>
              <span class="text-xs px-2 py-0.5 rounded-full border border-n-weak bg-n-alpha-2 text-n-slate-10">
                {{ typeLabel(proposal.proposal_type) }}
              </span>
            </div>
            <div class="flex items-center gap-3 mt-1">
              <span class="text-xs text-n-slate-10">
                Agente: {{ proposal.agent?.agent_name || '-' }}
              </span>
              <span v-if="proposal.confidence != null" class="text-xs" :class="confidenceColor(proposal.confidence)">
                Confianca: {{ (proposal.confidence * 100).toFixed(0) }}%
              </span>
              <span class="text-xs text-n-slate-10">{{ formatDate(proposal.created_at) }}</span>
            </div>
          </div>

          <!-- Action buttons -->
          <div class="flex items-center gap-1.5 shrink-0" @click.stop>
            <button
              v-if="proposal.status === 'pending'"
              class="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 dark:bg-emerald-900/20 transition-colors"
              :disabled="actionLoading.has(proposal.id)"
              @click="doAction(proposal.id, 'approve')"
            >
              Aprovar
            </button>
            <button
              v-if="proposal.status === 'pending'"
              class="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-10 hover:bg-n-alpha-2 transition-colors"
              :disabled="actionLoading.has(proposal.id)"
              @click="doAction(proposal.id, 'reject')"
            >
              Rejeitar
            </button>
            <button
              v-if="proposal.status === 'approved'"
              class="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 dark:bg-blue-900/20 transition-colors"
              :disabled="actionLoading.has(proposal.id)"
              @click="doAction(proposal.id, 'apply')"
            >
              Aplicar ao Prompt
            </button>
            <button
              v-if="proposal.status === 'applied'"
              class="px-2.5 py-1.5 text-xs font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              :disabled="actionLoading.has(proposal.id)"
              @click="doAction(proposal.id, 'rollback')"
            >
              Rollback
            </button>
          </div>
        </div>

        <!-- Expanded Detail -->
        <div v-if="expandedId === proposal.id" class="border-t border-n-weak p-4 bg-n-alpha-1">
          <div v-if="loadingDetail" class="flex items-center justify-center py-6">
            <span class="i-lucide-loader-2 w-5 h-5 text-n-slate-10 animate-spin" />
          </div>
          <div v-else-if="expandedData" class="flex flex-col gap-4">
            <!-- Proposed Changes -->
            <div>
              <h3 class="text-xs font-semibold text-n-slate-10 uppercase mb-2">Alteracao Proposta no Prompt</h3>
              <pre class="text-sm bg-n-solid-2 border border-n-weak rounded-lg p-3 whitespace-pre-wrap text-n-slate-12 overflow-x-auto">{{ expandedData.proposed_changes }}</pre>
            </div>

            <!-- Evidence -->
            <div>
              <h3 class="text-xs font-semibold text-n-slate-10 uppercase mb-2">Evidencia</h3>
              <p class="text-sm text-n-slate-11">{{ expandedData.evidence_summary }}</p>
            </div>

            <!-- Expected Impact -->
            <div v-if="expandedData.expected_impact">
              <h3 class="text-xs font-semibold text-n-slate-10 uppercase mb-2">Impacto Esperado</h3>
              <p class="text-sm text-n-slate-11">{{ expandedData.expected_impact }}</p>
            </div>

            <!-- Performance Before/After -->
            <div v-if="expandedData.performance_before && Object.keys(expandedData.performance_before).length > 0" class="grid grid-cols-2 gap-3">
              <div class="p-3 rounded-lg border border-n-weak bg-n-solid-2">
                <h4 class="text-xs font-semibold text-n-slate-10 mb-2">Antes de Aplicar</h4>
                <div class="flex flex-col gap-1 text-sm">
                  <span class="text-n-slate-11">Score: {{ expandedData.performance_before.avg_ai_score ?? '-' }}</span>
                  <span class="text-n-slate-11">Convertidos: {{ expandedData.performance_before.converted ?? '-' }}</span>
                  <span class="text-n-slate-11">Perdidos: {{ expandedData.performance_before.lost ?? '-' }}</span>
                </div>
              </div>
              <div v-if="expandedData.performance_after && Object.keys(expandedData.performance_after).length > 0" class="p-3 rounded-lg border border-n-weak bg-n-solid-2">
                <h4 class="text-xs font-semibold text-n-slate-10 mb-2">Depois de Aplicar</h4>
                <div class="flex flex-col gap-1 text-sm">
                  <span class="text-n-slate-11">Score: {{ expandedData.performance_after.avg_ai_score ?? '-' }}</span>
                  <span class="text-n-slate-11">Convertidos: {{ expandedData.performance_after.converted ?? '-' }}</span>
                  <span class="text-n-slate-11">Perdidos: {{ expandedData.performance_after.lost ?? '-' }}</span>
                </div>
              </div>
            </div>

            <!-- Meta -->
            <div class="flex items-center gap-4 text-xs text-n-slate-10 pt-2 border-t border-n-weak">
              <span v-if="expandedData.model_used">Modelo: {{ expandedData.model_used }}</span>
              <span v-if="expandedData.approved_by">Aprovado por: {{ expandedData.approved_by }}</span>
              <span v-if="expandedData.applied_at">Aplicado: {{ formatDate(expandedData.applied_at) }}</span>
              <span v-if="expandedData.rolled_back_at">Revertido: {{ formatDate(expandedData.rolled_back_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
